import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { PayoutJobSummary } from "@/services/installerPayouts.service";

export interface InstallerInvoicePDFComputedAmount {
  baseAmount: number;
  changeOrderAmount: number;
  fieldMaterialAmount: number;
  incentiveAmount: number;
  totalAmount: number;
  currency?: string;
}

export interface InstallerInvoicePDFData {
  invoiceNumber: string;
  vendorName: string;
  jobDisplayRef: string;
  job: PayoutJobSummary | null;
  computed: InstallerInvoicePDFComputedAmount;
  generatedAt?: string;
}

/**
 * Minimal internal AP record for a Koredor-generated ("auto_generated")
 * installer invoice — not a polished customer-facing document. Line-item
 * inclusion mirrors installer-invoice-submit's lineItems construction (base
 * always shown; change order/field material/incentive rows only when
 * non-zero) — keep in sync if that server-side logic changes.
 */
export function generateInstallerInvoicePDF(data: InstallerInvoicePDFData): Blob {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const currency = data.computed.currency ?? "USD";
  const money = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  let y = 20;
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Koredor — Installer Invoice", pageWidth / 2, y, { align: "center" });
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Invoice #: ${data.invoiceNumber}`, 20, y);
  doc.text(`Date: ${new Date(data.generatedAt ?? Date.now()).toLocaleDateString()}`, pageWidth - 20, y, { align: "right" });
  y += 6;
  doc.text(`Vendor: ${data.vendorName}`, 20, y);
  y += 6;
  doc.text(`Job: ${data.jobDisplayRef}`, 20, y);
  y += 6;
  if (data.job) {
    const jobLine = [data.job.customerName, data.job.projectType, [data.job.city, data.job.state].filter(Boolean).join(", ")]
      .filter(Boolean)
      .join(" · ");
    if (jobLine) {
      doc.text(jobLine, 20, y);
      y += 6;
    }
  }
  y += 4;

  const lineItems: Array<[string, string]> = [["Installation payout", money(data.computed.baseAmount)]];
  if (data.computed.changeOrderAmount !== 0) {
    lineItems.push(["Approved change orders", money(data.computed.changeOrderAmount)]);
  }
  if (data.computed.fieldMaterialAmount !== 0) {
    lineItems.push(["Field materials reimbursement", money(data.computed.fieldMaterialAmount)]);
  }
  if (data.computed.incentiveAmount !== 0) {
    lineItems.push(["Approved incentives", money(data.computed.incentiveAmount)]);
  }

  autoTable(doc, {
    startY: y,
    head: [["Description", `Amount (${currency})`]],
    body: lineItems,
    foot: [["Total", money(data.computed.totalAmount)]],
    theme: "striped",
    headStyles: { fillColor: [41, 128, 185] },
    footStyles: { fillColor: [230, 230, 230], textColor: 0, fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 120 }, 1: { cellWidth: 40, halign: "right" } },
    margin: { left: 20, right: 20 },
  });

  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text("Auto-generated by Koredor staff on behalf of the installer.", pageWidth / 2, 285, { align: "center" });

  return doc.output("blob");
}
